import { FC } from "react";

import { BASE_SIZES, SHOES_BASE_SIZES } from "../../../../constants/modal";
import { font, femaleBraScreenUI } from "../../../../configs";

import ArrowIcon from "../../../../assets/icons/ArrowIcon";
import { findBrandByDomain } from "../../../../configs/configLoader";

import * as React from "react";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import GivenchyBaseSizeSelector from "./components/GivenchyBaseSizeSelector";

import "./index.css";

interface IPropsBaseSizeSelector {
  sizeCountry: string;
  setSizeCountry: React.Dispatch<React.SetStateAction<string>>;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const BaseSizeSelector: FC<IPropsBaseSizeSelector> = ({
  sizeCountry,
  setSizeCountry,
  open,
  setOpen,
}) => {
  const brandDefined = findBrandByDomain();

  const urlParameters = new URLSearchParams(window.location.search);
  const isFootwear =
    urlParameters.get("category") === "footwear" ||
    urlParameters.get("footwear") === "true";
  const countryCode = urlParameters.get("countryCode");

  const isGivenchy = brandDefined?.name === "Givenchy";
  const isDevernois = brandDefined?.name === "Devernois";

  const isDevernoisEU = isDevernois && countryCode === "EU";

  const fontFamily = `${font}, sans-serif`;

  const currentState = open ? "open" : "closed";

  const langDropdownStyles: React.CSSProperties = {
    fontSize: femaleBraScreenUI.sizeSystem[currentState].fontSize,
    color: femaleBraScreenUI.sizeSystem[currentState].fontColor,
    borderColor: femaleBraScreenUI.sizeSystem[currentState].borderColor,
    borderWidth: femaleBraScreenUI.sizeSystem[currentState].borderWidth,
    borderRadius: femaleBraScreenUI.sizeSystem[currentState].borderRadius,
    letterSpacing: (femaleBraScreenUI.sizeSystem[currentState] as any)
      ?.letterSpacing,
  };

  const EUROPE_BRA_SIZES = BASE_SIZES.filter((size) =>
    ["EU", "IT", "FR"].includes(size.name),
  );

  const SIZE_LIST = isDevernoisEU
    ? EUROPE_BRA_SIZES
    : isFootwear
      ? SHOES_BASE_SIZES
      : BASE_SIZES;

  const handleMenuListKeyDown = (e: React.KeyboardEvent<HTMLUListElement>) => {
    if (e.key === "Tab") {
      e.preventDefault();

      const items = Array.from(
        e.currentTarget.querySelectorAll<HTMLElement>(
          '[role="option"]:not([aria-disabled="true"])',
        ),
      );
      if (!items.length) return;

      const activeIndex = items.findIndex(
        (item) => item === document.activeElement,
      );
      const nextIndex = e.shiftKey
        ? (activeIndex - 1 + items.length) % items.length
        : (activeIndex + 1) % items.length;

      items[nextIndex].focus();
    }
  };

  const borderRadiusValue = langDropdownStyles.borderRadius
    ? parseFloat(String(langDropdownStyles.borderRadius))
    : 0;

  return isGivenchy ? (
    <GivenchyBaseSizeSelector
      sizeCountry={sizeCountry}
      setSizeCountry={setSizeCountry}
      open={open}
      setOpen={setOpen}
      langDropdownStyles={langDropdownStyles}
    />
  ) : (
    <Select
      role="size-country-select"
      value={sizeCountry}
      tabIndex={-1}
      open={open}
      onOpen={() => {
        setOpen(true);
        requestAnimationFrame(() => {
          const firstItem = document.querySelector(
            '[role="option"]',
          ) as HTMLElement;
          firstItem?.focus();
        });
      }}
      onClose={() => setOpen(false)}
      onChange={(event) => {
        setSizeCountry(event.target.value);
        setOpen(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setOpen((prev) => !prev);
        }
      }}
      className="breasts__select"
      IconComponent={(props) => (
        <ArrowIcon
          style={{
            transform: open ? "rotate(90deg)" : "rotate(270deg)",
            position: "relative",
            top: "0px",
            right: "10px",
          }}
          open={open}
          {...props}
        />
      )}
      MenuProps={{
        autoFocus: true,
        disableAutoFocusItem: false,
        disableEnforceFocus: false,
        MenuListProps: {
          onKeyDown: handleMenuListKeyDown,
          sx: {
            fontFamily,
            textAlign: "center",
            padding: borderRadiusValue > 0 && open ? "0 5px" : 0,
          },
        },
        PaperProps: {
          sx: {
            fontFamily,
            textAlign: "center",
            border: `${langDropdownStyles.borderWidth} solid ${langDropdownStyles.borderColor}`,
            borderTopColor: "transparent",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            boxShadow: "none !important",
            transform: "translateY(-5px) !important",
            borderBottomLeftRadius: langDropdownStyles.borderRadius,
            borderBottomRightRadius: langDropdownStyles.borderRadius,
            paddingBottom: borderRadiusValue > 0 && open ? "15px" : 0,
          },
        },
      }}
      sx={{
        fontFamily,
        border: `${langDropdownStyles.borderWidth} solid ${langDropdownStyles.borderColor}`,
        borderBottomColor: open
          ? "transparent"
          : langDropdownStyles.borderColor,
        borderRadius: open
          ? `${langDropdownStyles.borderRadius} ${langDropdownStyles.borderRadius} 0 0`
          : langDropdownStyles.borderRadius,
        padding: borderRadiusValue > 0 && open ? "20px 5px" : 0,
        "&:focus-within": {
          borderColor: "#000",
          boxShadow: "0 0 0 2px rgba(0, 0, 0, 0.3)",
        },
      }}
    >
      {SIZE_LIST?.map(({ name }) => (
        <MenuItem
          key={name}
          className="breasts__select__item"
          value={name}
          data-value={name}
          style={{ fontFamily }}
          tabIndex={0}
          disabled={name === sizeCountry}
          onKeyDown={(e) => {
            if (e.key === "Enter" && name !== sizeCountry) {
              setSizeCountry(name);
              setOpen(false);
            }
          }}
        >
          <span
            className="breasts__select__item__flag-icons"
            style={{
              color: femaleBraScreenUI.sizeSystem.closed.fontColor,
              position: "relative",
              left: brandDefined?.name === "Victoria Beckham" ? "12px" : "8px",
              fontStyle:
                brandDefined?.name === "Victoria Beckham" ? "normal" : "",
            }}
          >
            {name}
          </span>
        </MenuItem>
      ))}
    </Select>
  );
};

export default BaseSizeSelector;
